import { $isTextNode, TextNode } from "lexical";
import { ListItemNode } from "@lexical/list"; // You must install lexical/list if not already
import { parseAllowedFontSize } from "../common/RichTextEditor/LexicalEditor/plugins/ToolbarPlugin/fontSize";
import { parseAllowedColor } from "../common/RichTextEditor/LexicalEditor/ui/ColorPicker";

function getTextAlign(element) {
  return element.style.textAlign || "";
}

export function getExtraStyles(element) {
  let extraStyles = "";
  const fontSize = parseAllowedFontSize(element.style.fontSize);
  const backgroundColor = parseAllowedColor(element.style.backgroundColor);
  const color = parseAllowedColor(element.style.color);

  if (fontSize !== "" && fontSize !== "15px") {
    extraStyles += `font-size: ${fontSize};`;
  }
  if (backgroundColor !== "" && backgroundColor !== "rgb(255, 255, 255)") {
    extraStyles += `background-color: ${backgroundColor};`;
  }
  if (color !== "" && color !== "rgb(0, 0, 0)") {
    extraStyles += `color: ${color};`;
  }

  return extraStyles;
}

export function buildImportMap() {
  const importMap = {};

  // Patch TextNode importers
  for (const [tag, fn] of Object.entries(TextNode.importDOM() || {})) {
    importMap[tag] = (importNode) => {
      const importer = fn(importNode);
      if (!importer) return null;

      return {
        ...importer,
        conversion: (element) => {
          const output = importer.conversion(element);
          if (
            output === null ||
            output.forChild === undefined ||
            output.after !== undefined ||
            output.node !== null
          ) {
            return output;
          }

          const extraStyles = getExtraStyles(element);
          const textTransform = element.style.textTransform;

          if (extraStyles || textTransform) {
            const { forChild } = output;
            return {
              ...output,
              forChild: (child, parent) => {
                const textNode = forChild(child, parent);
                if ($isTextNode(textNode)) {
                  // Handle text transform as Lexical format
                  if (textTransform === "uppercase") {
                    textNode.setFormat("uppercase");
                  } else if (textTransform === "lowercase") {
                    textNode.setFormat("lowercase");
                  } else if (textTransform === "capitalize") {
                    textNode.setFormat("capitalize");
                  }

                  // Handle other styles as CSS
                  if (extraStyles) {
                    textNode.setStyle(textNode.getStyle() + extraStyles);
                  }
                }
                return textNode;
              },
            };
          }
          return output;
        },
      };
    };
  }

  // Patch ListItemNode to preserve `text-align`
  const listItemImporters = ListItemNode.importDOM?.() || {};
  for (const [tag, fn] of Object.entries(listItemImporters)) {
    importMap[tag] = (importNode) => {
      const importer = fn(importNode);
      if (!importer) return null;

      return {
        ...importer,
        conversion: (element) => {
          const output = importer.conversion(element);
          if (!output || !output.node) return output;

          const align = getTextAlign(element);
          if (align) {
            output.node.setFormat?.(align);
          }

          return output;
        },
      };
    };
  }

  return importMap;
}

 export const cleanBaselineHtml = (rawHtml) => {
        if (!rawHtml) return "";
        let cleaned = rawHtml.replace(/<div class="editor-shell">/g, "").replace(/<\/div>\s*$/, "");
        try {
          const parser = new DOMParser();
          const dom = parser.parseFromString(`<div>${cleaned}</div>`, "text/html");
          const container = dom.body.firstElementChild;
          if (container) {
            // Remove editor UI that should never be persisted/rendered as content.
            container
              .querySelectorAll(
                ".toolbars, .toolbar, .link-editor, .table-cell-action-button-container, .draggable-block-menu, .floating-text-format-popup, .floating-link-editor"
              )
              .forEach((el) => el.remove());

            // Unwrap editor layout shells so only user-authored content remains.
            container
              .querySelectorAll(".editor-containerss, .editor-scroller, .editor")
              .forEach((wrapper) => {
                const parent = wrapper.parentNode;
                if (!parent) return;
                while (wrapper.firstChild) {
                  parent.insertBefore(wrapper.firstChild, wrapper);
                }
                parent.removeChild(wrapper);
              });

            // Remove leftover interactive controls that can appear in malformed editor HTML.
            container
              .querySelectorAll("button, select, option")
              .forEach((el) => el.remove());

            container.querySelectorAll("p").forEach(p => {
              p.classList.remove("PlaygroundEditorTheme__paragraph");
              p.removeAttribute("dir");
              if (p.getAttribute('class') === '') p.removeAttribute('class');
            });
            container.querySelectorAll("span[data-lexical-text]").forEach(span => {
              span.removeAttribute("data-lexical-text");
              if (!span.hasAttributes()) {
                const fragment = document.createDocumentFragment();
                while (span.firstChild) {
                  fragment.appendChild(span.firstChild);
                }
                span.parentNode.replaceChild(fragment, span);
              }
            });
            cleaned = container.innerHTML;
          }
        } catch (e) {}
        
        const isEmpty = cleaned.replace(/<p[^>]*>(\s|<br>|&nbsp;)*<\/p>/gi, '').trim() === '';
        return isEmpty ? "" : cleaned;
      };
      
